/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { Label } from 'src/modules/labeling/types/Label';

/**
 * All the actions used in the entity dropdown/menu wrapped in one object.
 */
export type MenuActionItems = ReturnType<typeof menuActionsGenerator>;

/**
 * Passed in params to the menuActionsGenerator
 */
export type ActionsGeneratorParams = {
    /**
     * Callback functions for actions click event
     */
    callbackFunctions: {
        /**
         * Un label the clicked checked items
         */
        onUnLabel: (entities: Entity[]) => void;

        /**
         * Confirm the predictions of the selected tokens
         */
        confirmPrediction: (includeSubentities: boolean) => void;

        /**
         * Go to the patterns page
         */
        openPatternsPage: () => void;

        /**
         * Opens the entity palette
         */
        openEntityPane: () => void;

        /**
         * Open the prebuilt entities modal
         */
        openPrebuiltEntityModal: () => void;
    };

    /**
     * Localization function to generate localized text
     */
    localize: ReturnType<typeof useLocalization>['localize'];

    /**
     * The clicked label
     */
    clickedOnLabel: Label;

    /**
     * Determines if the entity palette open or not
     */
    isEntityPaneOpen: boolean;
};

const messages = defineMessages({
    confirmPredictions: {
        id: 'labelling.entityDropdown.confirmPredictions',
        defaultMessage: 'Confirm predictions'
    },
    confirmEntityPrediction: {
        id: 'labelling.entityDropdown.confirmEntityPrediction',
        defaultMessage: 'Confirm {entityName}'
    },
    confirmSubentitiesPrediction: {
        id: 'labelling.entityDropdown.confirmSubentitiesPrediction',
        defaultMessage: 'Confirm {entityName} and subentities'
    },
    unlabel: {
        id: 'labelling.entityDropdown.unlabel',
        defaultMessage: 'Unlabel'
    },
    removeSynonym: {
        id: 'labelling.entityDropdown.removeSynonym',
        defaultMessage: 'Remove the synonym '
    },
    openPatterns: {
        id: 'labelling.entityDropdown.openPatterns',
        defaultMessage: 'Open patterns page '
    },
    openEntityPalette: {
        id: 'labelling.entityDropdown.openEntityPalette',
        defaultMessage: 'Open entity pane'
    },
    viewInEntityPalette: {
        id: 'labelling.entityDropdown.viewInEntityPalette',
        defaultMessage: 'View in entity pane'
    },
    openPrebuiltEntityModal: {
        id: 'labelling.entityDropdown.openPrebuiltEntitiesModal',
        defaultMessage: 'Open prebuilt entity ...'
    }
});

/**
 * Returns the list of actions to be displayed in the entity menu.
 */
export const menuActionsGenerator = (params: ActionsGeneratorParams) => {
    const { callbackFunctions, localize, clickedOnLabel, isEntityPaneOpen } = params;
    const { onUnLabel, confirmPrediction, openPatternsPage, openEntityPane, openPrebuiltEntityModal } = callbackFunctions;

    /**
     * An action to unlabel an already labeled entity.
     */
    const unLabelAction = (entities?: Entity[]) => ({
        key: 'unlabel',
        iconProps: {
            iconName: 'RevToggleKey'
        },
        text: localize(messages.unlabel),
        onClick: () => {
            if (entities) {
                onUnLabel(entities);

                return;
            }

            if (clickedOnLabel) {
                onUnLabel([clickedOnLabel.entity]);
            }
        }
    });

    /**
     * An action to confirm the prediction of the selected entity.
     */
    const confirmPredictionAction = (entityName?: string) => {
        const text = entityName ? localize(messages.confirmEntityPrediction, { entityName }) : localize(messages.confirmPredictions);

        return {
            key: 'confirmPrediction',
            text,
            onClick: () => confirmPrediction(false)
        };
    };

    /**
     * An action to confirm the prediction of the entity children.
     */
    const confirmSubEntitiesPredictionAction = (entityName: string) => ({
        key: 'confirmSubEntitiesPrediction',
        text: localize(messages.confirmSubentitiesPrediction, { entityName }),
        onClick: () => confirmPrediction(true)
    });

    /**
     * An action to remove the synonym of the selected entity.
     */
    const removeSynonymAction: IContextualMenuItem = {
        key: 'removeSynonym',
        iconProps: {
            iconName: 'Delete'
        },
        text: localize(messages.removeSynonym)
    };

    /**
     * An action to open the patterns page.
     */
    const openPatternsAction: IContextualMenuItem = {
        key: 'openPatterns',
        iconProps: {
            iconName: 'QuestionnaireMirrored'
        },
        text: localize(messages.openPatterns),
        onClick: openPatternsPage
    };

    /**
     * An action to open the prebuilt entities modal.
     */
    const openPrebuiltEntityModalAction: IContextualMenuItem = {
        key: 'prebuiltEntities',
        iconProps: {
            iconName: 'Package'
        },
        text: localize(messages.openPrebuiltEntityModal),
        onClick: openPrebuiltEntityModal
    };

    /**
     * A function that returns an action to open the entity palette displayed with a specified message.
     * @param message view/open entity palette.
     */
    const entityPaneAction = (message: 'open' | 'view'): IContextualMenuItem => ({
        key: 'entityPalette',
        disabled: isEntityPaneOpen,
        iconProps: {
            iconName: 'CustomList'
        },
        text: localize(message === 'open' ? messages.openEntityPalette : messages.viewInEntityPalette),
        onClick: openEntityPane
    });

    return {
        unLabelAction,
        confirmPredictionAction,
        confirmSubEntitiesPredictionAction,
        removeSynonymAction,
        openPatternsAction,
        openPrebuiltEntityModalAction,
        entityPaneAction
    };
};
